import PropTypes from 'prop-types';
import React from 'react';
import { navigate, useIntl } from 'gatsby-plugin-intl';
import { graphql } from 'gatsby';

import AdvantagesBlock from '../components/content/AdvantagesBlock/AdvantagesBlock';
import ClassesBlock from '../components/content/ClassesBlock/ClassesBlock';
import ComingSoon from '../components/content/ComingSoon/ComingSoon';
import Config from '../config';
import CtaSticky from '../components/content/CtaSticky/CtaSticky';
import Footer from '../components/layout/footer/Footer';
import HeroTemp from '../components/content/Hero/HeroTemp';
import HomeArgumentsBlock from '../components/content/HomeArgumentsBlock/HomeArgumentsBlock';
import Navigation from '../components/layout/Navigation/Navigation';
import PassCTABlock from '../components/content/PassCTABlock/PassCTABlock';
import SEO from '../components/SEO';
import TestimonialsBlock from '../components/content/TestimonialsBlock/TestimonialsBlock';
import ValuesBlock from '../components/content/ValuesBlock/ValuesBlock';
import { injectUser } from '../components/session';
import { translateLocal } from '../i18n';

import '../stylesheets/Homepage.scss';

const IndexPage = ({ authUser, data, isAuthReady }) => {
  const intl = useIntl();
  const lang = intl.locale;

  const { pages: homepageData, passPage } = data;

  const {
    description, sections, title, seo,
  } = homepageData;

  const [isHeroVisible, setIsHeroVisible] = React.useState(true);
  const [tesimonials] = React.useState(data.allTestimonials.nodes.sort(() => 0.5 - Math.random()).slice(0, 3));

  // If user has already purshased, redirect him to dashboard
  React.useEffect(() => {
    if (authUser && (authUser.isFullPassActive || authUser.hasAClass)) {
      navigate(`${Config.routePaths.dashboard}`, { replace: true });
    }
  }, [authUser]);

  const allClasses = data.allClasses.nodes.sort((a, b) => a.order - b.order).filter((item) => !!item.teaser).map((item) => ({
    ...item.teaser,
    heroImage: item.heroImage,
    teaserImage: item.teaserImage,
    sections: item.sections,
    slug: item.slug,
    id: item.id,
    featured: item.featured,
    featuredCard: item.teaser?.featured,
    isPreSale: item.isPreSale,
    preSaleLabel: translateLocal(item.preSaleDispoLabel, lang),
    order: item.order,
  }));

  const classTeasers = allClasses.filter((item) => !item.isPreSale);

  sections.classes.items = classTeasers;

  const preSalesClasses = allClasses.filter((item) => !!item.isPreSale);

  if (preSalesClasses.length === 1) {
    preSalesClasses[0] = {
      ...preSalesClasses[0],
      featuredCard: true,
    };
  }

  sections.preSales.items = preSalesClasses;

  sections.ctaBlock.imageLeft = passPage.sections.passBlock.imageLeft;
  sections.ctaBlock.imageRight = passPage.sections.passBlock.imageRight;

  const onHeroVisibilityChange = (isVisible) => {
    setIsHeroVisible(isVisible);
  };

  return (
    <div className={`homepage ${isAuthReady ? '' : 'hidden'}`}>
      <SEO
        title={translateLocal(title, lang)}
        description={translateLocal(description, lang)}
        image={seo.image.src}
      />

      <Navigation
        routes={Config.routePaths}
        sticky={false}
        opaque
        pushTop={false}
      />

      {/*
        Ici on a remplacé le hero de la homepage par celui du pass pour pouvoir afficher la mosaique avec les nouvelles stars
        tout en gardant le composant lié au carousel pour plus tard
      */}

      <HeroTemp
        videoId={sections.hero.videoId}
        title={translateLocal(sections.hero.title)}
        description={translateLocal(sections.hero.description)}
        cta={sections.hero.cta}
        onVisibilityChange={onHeroVisibilityChange}
        medias={passPage.sections.hero.medias}
        testimonials={passPage.sections.hero.testimonials}
      />

      {sections.preSales && sections.preSales.items && sections.preSales.items.length > 0 && (
        <ClassesBlock
          data={sections.preSales}
          withLinks
          id="presales"
        />
      )}

      {sections.classes && sections.classes.items && (
        <ClassesBlock
          data={sections.classes}
          id="classes"
          withLinks
          limit={6}
          showPlusButton
          plusButtonTarget={Config.routePaths.allClasses}
          inverseBackground
        />
      )}

      <AdvantagesBlock
        data={passPage.sections.advantages}
      />

      {!!sections.comingSoon && !!data.allClassesComingSoon && !!data.allClassesComingSoon.nodes && !!data.allClassesComingSoon.nodes.length && (
        <ComingSoon
          pageData={sections.comingSoon}
          items={data.allClassesComingSoon.nodes}
        />
      )}

      <HomeArgumentsBlock
        data={sections.arguments}
      />

      {sections.testimonials && data.allTestimonials.nodes && (
        <TestimonialsBlock
          data={tesimonials}
          pageData={sections.testimonials}
          black
        />
      )}

      <PassCTABlock
        data={sections.ctaBlock}
        link={Config.routePaths.allClasses}
      />

      {sections.values && (
        <ValuesBlock
          items={sections.values.items}
        />
      )}

      <Footer />

      {!isHeroVisible && (
        <CtaSticky />
      )}
    </div>
  );
};

IndexPage.propTypes = {
  authUser: PropTypes.object,
  data: PropTypes.object.isRequired,
  isAuthReady: PropTypes.bool,
};

IndexPage.defaultProps = {
  authUser: null,
  isAuthReady: false,
};

export default injectUser(
  IndexPage,
);

export const data = graphql`
  query HomePageQuery {
    pages(id: {eq: "home"}) {
      id
      description {
        fr
        en
        es
      }
      slug
      seo {
        image {
          src
        }
      }
      title {
        fr
        en
        es
      }
      sections {
        hero {
          cta {
            description {
              fr
              en
              es
            }
            emailPlaceholder {
              fr
              en
              es
            }
            label {
              fr
              en
              es
            }
            labelAllAccess {
              fr
              en
              es
            }
            labelClass {
              fr
              en
              es
            }
            url
          }
          description {
            fr
            en
            es
          }
          slug
          title {
            fr
            en
            es
          }
          videoId
        }
        intro {
          items {
            description {
              fr
              en
              es
            }
            image
            title {
              fr
              en
              es
            }
          }
          subtitle {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        preSales {
          subtitle {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        classes {
          items {
            description {
              fr
              en
              es
            }
            domain {
              fr
              en
              es
            }
            image {
              src
            }
            name
            teaches {
              fr
              en
              es
            }
          }
          subtitle {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        comingSoon {
          subtitle {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        testimonials {
          subtitle {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        arguments {
          title {
            fr
            en
            es
          }
          subtitle {
            fr
            en
            es
          }
          items {
            image {
              src
            }
            title {
              fr
              en
              es
            }
            text {
              fr
              en
              es
            }
          }
        }
        ctaBlock {
          title {
            fr
            en
            es
          }
          buttonLabel {
            fr
            en
            es
          }
        }
        values {
          items {
            description {
              fr
              en
              es
            }
            icon
            title {
              fr
              en
              es
            }
          }
        }
      }
    }
    passPage: pages(id: {eq: "passFullAccess"}) {
      sections {
        hero {
          medias {
            image {
              src
            }
          }
          testimonials {
            fr {
              text
              author
            }
            en {
              text
              author
            }
            es {
              text
              author
            }
          }
        }
        advantages {
          items {
            text {
              fr
              en
              es
            }
            title {
              fr
              en
              es
            }
          }
        }
        passBlock {
          imageLeft {
            src
          }
          imageRight {
            src
          }
        }
      }
    }
    allClasses {
      nodes {
        featured
        id
        isPreSale
        order
        preSaleDispoLabel {
          fr
          en
          es
        }
        slug
        teaser {
          description {
            fr
            en
            es
          }
          domain {
            fr
            en
            es
          }
          image {
            src
            alt {
              fr
              en
              es
            }
          }
          name
          teaches {
            fr
            en
            es
          }
          isNew
          featured
          title {
            fr
            en
            es
          }
        }
        heroImage {
          childImageSharp {
            fluid(quality: 95, maxWidth: 2000) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        teaserImage {
          childImageSharp {
            fluid(quality: 95, maxWidth: 1120) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    allClassesComingSoon {
      nodes {
        description {
          fr
          en
          es
        }
        domain {
          fr
          en
          es
        }
        image {
          src
        }
        name
        order
        teaches {
          fr
          en
          es
        }
        teaserImage {
          childImageSharp {
            fluid(quality: 95, maxWidth: 540) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    allTestimonials {
      nodes {
        name
        text {
          fr
          en
          es
        }
        rating
      }
    }
  }
`;
